import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import TextField from "../TextField";
import { apiRequestNoAuth } from "../../util/util";

// T-011: Normalize phone number to E.164 format for storage
function normalizePhone(phone) {
  if (!phone) return "";
  // Remove all non-digit characters except leading +
  const hasPlus = phone.trim().startsWith("+");
  const digits = phone.replace(/\D/g, "");

  // If 10 digits, assume US number
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  // If 11 digits starting with 1, it's US with country code
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  // Otherwise preserve what they entered with + prefix if they had one
  return hasPlus ? `+${digits}` : digits;
}

function LeadCaptureForm({ onValidated, onFormChange }) {
  const {
    register,
    formState: { errors, isValid },
    watch,
  } = useForm({ mode: "onChange" });
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [emailValidated, setEmailValidated] = useState(false);
  const [emailChecked, setEmailChecked] = useState(""); // Track which email was validated

  const emailField = register("email", {
    required: "Email is required",
    pattern: {
      value: /\S+@\S+\.\S+/,
      message: "Invalid email format",
    },
  });

  // Watch all fields
  const formData = watch();
  const { firstName, lastName, email, phone, consent } = formData;

  // Editing the email invalidates any earlier result. The server check runs
  // on blur (below), not on every typing pause, so one office IP does not burn
  // the existence-check budget while several agents type.
  useEffect(() => {
    if (email !== emailChecked) {
      setEmailValidated(false);
      setValidationError("");
    }
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailBlur = () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    if (email === emailChecked && (emailValidated || validationError)) return;
    validateEmail(email);
  };

  // Email validation function
  const validateEmail = async (emailToValidate) => {
    setValidating(true);
    setValidationError("");
    setEmailValidated(false);

    try {
      // Step 1: Check if user exists in Firebase
      const existsResponse = await apiRequestNoAuth("email", "POST", {
        action: "checkFirebaseExists",
        data: { email: emailToValidate },
      });

      if (existsResponse.exists) {
        setValidationError("You already have an account. Please sign in.");
        setEmailChecked(emailToValidate);
        onValidated(null, {
          isValidating: false,
          existingUser: true,
          isStateFarm: false,
          isValid: false,
        });
        setValidating(false);
        return;
      }

      // Step 2: Check State Farm email
      const sfResponse = await apiRequestNoAuth("email", "POST", {
        action: "checkStateFarm",
        data: { email: emailToValidate },
      });

      if (!sfResponse.success) {
        setValidationError(
          sfResponse.message || "Cannot use corporate email address"
        );
        setEmailChecked(emailToValidate);
        onValidated(null, {
          isValidating: false,
          existingUser: false,
          isStateFarm: true,
          isValid: false,
        });
        setValidating(false);
        return;
      }

      // Valid email
      setEmailValidated(true);
      setEmailChecked(emailToValidate);
      setValidating(false);
    } catch (error) {
      console.error("Email validation error:", error);
      setValidationError("Error validating email. Please try again.");
      setValidating(false);
    }
  };

  // Check if form is complete and notify parent
  const checkFormComplete = useCallback(() => {
    const isFormComplete =
      firstName &&
      lastName &&
      email &&
      /\S+@\S+\.\S+/.test(email) &&
      phone &&
      consent &&
      emailValidated &&
      !validationError;

    // T-011: Normalize phone number before passing to parent
    const normalizedPhone = normalizePhone(phone);

    if (onFormChange) {
      onFormChange({
        firstName,
        lastName,
        email,
        phone: normalizedPhone,
        consent,
      }, isFormComplete);
    }

    if (isFormComplete) {
      onValidated({
        firstName,
        lastName,
        email,
        phone: normalizedPhone,
        consent,
      }, {
        isValidating: false,
        existingUser: false,
        isStateFarm: false,
        isValid: true,
      });
    }
  }, [firstName, lastName, email, phone, consent, emailValidated, validationError, onValidated, onFormChange]);

  // Auto-validate when form changes
  useEffect(() => {
    checkFormComplete();
  }, [checkFormComplete]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          id="firstName"
          label="First Name"
          placeholder="John"
          error={errors.firstName}
          {...register("firstName", { required: "First name is required" })}
        />
        <TextField
          id="lastName"
          label="Last Name"
          placeholder="Doe"
          error={errors.lastName}
          {...register("lastName", { required: "Last name is required" })}
        />
      </div>

      <div className="relative">
        <TextField
          type="email"
          id="email"
          label="Email"
          placeholder="john@example.com"
          error={errors.email || (validationError && { message: validationError })}
          {...emailField}
          onBlur={(e) => {
            emailField.onBlur(e);
            handleEmailBlur();
          }}
        />
        {/* T-011: Email validation status indicator */}
        {email && /\S+@\S+\.\S+/.test(email) && (
          <div className="absolute right-3 top-9">
            {validating && (
              <span className="text-gray-400 text-sm">Checking...</span>
            )}
            {!validating && emailValidated && !validationError && (
              <span className="text-green-500">&#10003;</span>
            )}
          </div>
        )}
      </div>

      <TextField
        type="tel"
        id="phone"
        label="Phone"
        placeholder="(555) 123-4567"
        error={errors.phone}
        {...register("phone", {
          required: "Phone is required",
          pattern: {
            // T-011: More flexible phone pattern - allows digits, spaces, dashes, plus, parens, dots
            value: /^[\d\s\-\+\(\)\.]+$/,
            message: "Please enter a valid phone number",
          },
          minLength: {
            value: 7,
            message: "Phone number too short",
          },
        })}
      />
      <p className="text-xs text-gray-500 -mt-2">
        Any format accepted (e.g., 555-123-4567, (555) 123.4567, +1 555 123 4567)
      </p>

      <div className="flex items-start">
        <input
          type="checkbox"
          id="consent"
          className="mt-1 mr-2"
          {...register("consent", { required: "You must agree to continue" })}
        />
        <label htmlFor="consent" className="text-sm text-gray-600">
          I agree to receive communications about my purchase
        </label>
      </div>
      {errors.consent && (
        <p className="text-sm text-red-600 mt-1">{errors.consent.message}</p>
      )}
    </div>
  );
}

export default LeadCaptureForm;
